import { tgpu, d, std } from 'typegpu';
import type { TgpuRoot, TgpuBuffer, StorageFlag, UniformFlag } from 'typegpu';
import type { WgslArray } from 'typegpu/data';
import { SculptureStateA, SculptureStateB, SculptureParams, DataCell } from './types';
import { systemDeriv } from './deriv';
import { samplePerturb } from '../shared/hash';
import { NbParams } from '../shared/neighbor';
import type { NeighborPipelinesResult } from '../shared/DualStateSystem';

const TWO_PI = 6.28318530718;
const Rk4Result = d.struct({ t0: d.f32, w0: d.f32, t1: d.f32, w1: d.f32, t2: d.f32, w2: d.f32, t3: d.f32, w3: d.f32 });

export interface SculptureNeighborOpts {
  root: TgpuRoot;
  StateArrayA: WgslArray<typeof SculptureStateA>;
  StateArrayB: WgslArray<typeof SculptureStateB>;
  DataArray: WgslArray<typeof DataCell>;
  NbStackArrayA: WgslArray<typeof SculptureStateA>;
  NbStackArrayB: WgslArray<typeof SculptureStateB>;
  paramsBuffer: TgpuBuffer<typeof SculptureParams> & UniformFlag;
  nbParamsBuffer: TgpuBuffer<typeof NbParams> & UniformFlag;
  baseAa: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag;
  baseAb: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag;
  baseBa: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag;
  baseBb: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag;
  nbPertAa: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag;
  nbPertAb: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag;
  nbPertBa: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag;
  nbPertBb: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag;
  nbDataBuf: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag;
}

export function buildSculptureNeighborPipelines(o: SculptureNeighborOpts): NeighborPipelinesResult {
  const root = o.root;

  const initLayout = tgpu.bindGroupLayout({
    baseA: { storage: o.StateArrayA, access: 'mutable' }, baseB: { storage: o.StateArrayB, access: 'mutable' },
    pertA: { storage: o.NbStackArrayA, access: 'mutable' }, pertB: { storage: o.NbStackArrayB, access: 'mutable' },
    data: { storage: o.DataArray, access: 'mutable' },
    params: { uniform: SculptureParams }, nb: { uniform: NbParams },
  });
  const stepLayout = tgpu.bindGroupLayout({
    baseCurA: { storage: o.StateArrayA }, baseCurB: { storage: o.StateArrayB },
    baseNextA: { storage: o.StateArrayA, access: 'mutable' }, baseNextB: { storage: o.StateArrayB, access: 'mutable' },
    pertCurA: { storage: o.NbStackArrayA }, pertCurB: { storage: o.NbStackArrayB },
    pertNextA: { storage: o.NbStackArrayA, access: 'mutable' }, pertNextB: { storage: o.NbStackArrayB, access: 'mutable' },
    data: { storage: o.DataArray, access: 'mutable' },
    params: { uniform: SculptureParams }, nb: { uniform: NbParams },
  });
  const il = initLayout; const sl = stepLayout;

  const circDiff = (a: number) => { 'use gpu'; return a - std.floor(a / TWO_PI + 0.5) * TWO_PI; };

  const integrate = (t0: number, w0: number, t1: number, w1: number, t2: number, w2: number, t3: number, w3: number, sM: number, sL: number, sA: number, sR: number, sN: number, dt: number) => {
    'use gpu';
    const h = 0.5 * dt;
    const e1 = systemDeriv(t0, w0, t1, w1, t2, w2, t3, w3, sM, sL, sA, sR, sN);
    const e2 = systemDeriv(t0 + h * e1.dt0, w0 + h * e1.dw0, t1 + h * e1.dt1, w1 + h * e1.dw1, t2 + h * e1.dt2, w2 + h * e1.dw2, t3 + h * e1.dt3, w3 + h * e1.dw3, sM, sL, sA, sR, sN);
    const e3 = systemDeriv(t0 + h * e2.dt0, w0 + h * e2.dw0, t1 + h * e2.dt1, w1 + h * e2.dw1, t2 + h * e2.dt2, w2 + h * e2.dw2, t3 + h * e2.dt3, w3 + h * e2.dw3, sM, sL, sA, sR, sN);
    const e4 = systemDeriv(t0 + dt * e3.dt0, w0 + dt * e3.dw0, t1 + dt * e3.dt1, w1 + dt * e3.dw1, t2 + dt * e3.dt2, w2 + dt * e3.dw2, t3 + dt * e3.dt3, w3 + dt * e3.dw3, sM, sL, sA, sR, sN);
    const s6 = dt / 6.0;
    return Rk4Result({
      t0: t0 + s6 * (e1.dt0 + 2 * e2.dt0 + 2 * e3.dt0 + e4.dt0), w0: w0 + s6 * (e1.dw0 + 2 * e2.dw0 + 2 * e3.dw0 + e4.dw0),
      t1: t1 + s6 * (e1.dt1 + 2 * e2.dt1 + 2 * e3.dt1 + e4.dt1), w1: w1 + s6 * (e1.dw1 + 2 * e2.dw1 + 2 * e3.dw1 + e4.dw1),
      t2: t2 + s6 * (e1.dt2 + 2 * e2.dt2 + 2 * e3.dt2 + e4.dt2), w2: w2 + s6 * (e1.dw2 + 2 * e2.dw2 + 2 * e3.dw2 + e4.dw2),
      t3: t3 + s6 * (e1.dt3 + 2 * e2.dt3 + 2 * e3.dt3 + e4.dt3), w3: w3 + s6 * (e1.dw3 + 2 * e2.dw3 + 2 * e3.dw3 + e4.dw3),
    });
  };

  const nbInitCell = (ci: number) => {
    'use gpu';
    const p = il.$.params; const np = il.$.nb;
    const res = p.resolution;
    const x = ci % d.u32(res); const y = ci / d.u32(res);
    const u = d.f32(x) / res; const v = d.f32(y) / res;
    const omu = 1 - u; const omv = 1 - v;
    const t0 = omu * omv * p.cA00_t0 + u * omv * p.cA10_t0 + omu * v * p.cA01_t0 + u * v * p.cA11_t0;
    const w0 = omu * omv * p.cA00_w0 + u * omv * p.cA10_w0 + omu * v * p.cA01_w0 + u * v * p.cA11_w0;
    const t1 = omu * omv * p.cA00_t1 + u * omv * p.cA10_t1 + omu * v * p.cA01_t1 + u * v * p.cA11_t1;
    const w1 = omu * omv * p.cA00_w1 + u * omv * p.cA10_w1 + omu * v * p.cA01_w1 + u * v * p.cA11_w1;
    const t2 = omu * omv * p.cB00_t2 + u * omv * p.cB10_t2 + omu * v * p.cB01_t2 + u * v * p.cB11_t2;
    const w2 = omu * omv * p.cB00_w2 + u * omv * p.cB10_w2 + omu * v * p.cB01_w2 + u * v * p.cB11_w2;
    const t3 = omu * omv * p.cB00_t3 + u * omv * p.cB10_t3 + omu * v * p.cB01_t3 + u * v * p.cB11_t3;
    const w3 = omu * omv * p.cB00_w3 + u * omv * p.cB10_w3 + omu * v * p.cB01_w3 + u * v * p.cB11_w3;
    il.$.baseA[ci] = SculptureStateA({ theta0: t0, omega0: w0, theta1: t1, omega1: w1 });
    il.$.baseB[ci] = SculptureStateB({ theta2: t2, omega2: w2, theta3: t3, omega3: w3 });
    const K = d.u32(np.K); const cc = d.u32(res) * d.u32(res);
    const px = d.f32(x) / res; const py = d.f32(y) / res;
    for (let k = 0; k < K; k++) {
      const kf = d.f32(k);
      const h0 = samplePerturb(px * 1000 + np.seed + kf * 131.5, py * 1000 + np.seed, np.perturb, np.perturbMode);
      const h1 = samplePerturb(px * 1000 + 17 + np.seed + kf * 131.5, py * 1000 + np.seed, np.perturb, np.perturbMode);
      const h2 = samplePerturb(px * 1000 + 100 + np.seed + kf * 131.5, py * 1000 + np.seed, np.perturb, np.perturbMode);
      const h3 = samplePerturb(px * 1000 + 217 + np.seed + kf * 131.5, py * 1000 + np.seed, np.perturb, np.perturbMode);
      const idx = d.u32(k) * cc + ci;
      il.$.pertA[idx] = SculptureStateA({ theta0: t0 + h0, omega0: w0, theta1: t1 + h1, omega1: w1 });
      il.$.pertB[idx] = SculptureStateB({ theta2: t2 + h2, omega2: w2, theta3: t3 + h3, omega3: w3 });
    }
    il.$.data[ci] = DataCell({ r: 0, g: 0, b: 0, a: 0 });
  };



  const nbStepCell = (ci: number) => {
    'use gpu';
    const p = sl.$.params; const np = sl.$.nb; const dt = p.dt;
    const res = p.resolution; const cc = d.u32(res) * d.u32(res);
    const bA = sl.$.baseCurA[ci]; const bB = sl.$.baseCurB[ci];
    const bn = integrate(bA.theta0, bA.omega0, bA.theta1, bA.omega1, bB.theta2, bB.omega2, bB.theta3, bB.omega3, p.scM0, p.scL0, p.scA0, p.scR, p.scN, dt);
    sl.$.baseNextA[ci] = SculptureStateA({ theta0: bn.t0, omega0: bn.w0, theta1: bn.t1, omega1: bn.w1 });
    sl.$.baseNextB[ci] = SculptureStateB({ theta2: bn.t2, omega2: bn.w2, theta3: bn.t3, omega3: bn.w3 });
    const mode = np.nbMode;
    const K = d.u32(np.K);
    const px = d.f32(ci % d.u32(res)) / res; const py = d.f32(ci / d.u32(res)) / res;
    if (mode < 1.5) {
      let best = 0.0;
      for (let k = 0; k < K; k++) {
        const idx = d.u32(k) * cc + ci;
        const pA = sl.$.pertCurA[idx]; const pB = sl.$.pertCurB[idx];
        const pn = integrate(pA.theta0, pA.omega0, pA.theta1, pA.omega1, pB.theta2, pB.omega2, pB.theta3, pB.omega3, p.scM0, p.scL0, p.scA0, p.scR, p.scN, dt);
        sl.$.pertNextA[idx] = SculptureStateA({ theta0: pn.t0, omega0: pn.w0, theta1: pn.t1, omega1: pn.w1 });
        sl.$.pertNextB[idx] = SculptureStateB({ theta2: pn.t2, omega2: pn.w2, theta3: pn.t3, omega3: pn.w3 });
        const dd0 = circDiff(pn.t0 - bn.t0); const dw0 = pn.w0 - bn.w0;
        const dd1 = circDiff(pn.t1 - bn.t1); const dw1 = pn.w1 - bn.w1;
        const dd2 = circDiff(pn.t2 - bn.t2); const dw2 = pn.w2 - bn.w2;
        const dd3 = circDiff(pn.t3 - bn.t3); const dw3 = pn.w3 - bn.w3;
        const dist = std.sqrt(dd0*dd0 + dw0*dw0 + dd1*dd1 + dw1*dw1 + dd2*dd2 + dw2*dw2 + dd3*dd3 + dw3*dw3);
        if (dist > best) best = dist;
      }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: best, a: 1 });
    } else if (mode < 2.5) {
      let acc = 0.0;
      for (let k = 0; k < K; k++) {
        const idx = d.u32(k) * cc + ci;
        const pA = sl.$.pertCurA[idx]; const pB = sl.$.pertCurB[idx];
        const pn = integrate(pA.theta0, pA.omega0, pA.theta1, pA.omega1, pB.theta2, pB.omega2, pB.theta3, pB.omega3, p.scM0, p.scL0, p.scA0, p.scR, p.scN, dt);
        sl.$.pertNextA[idx] = SculptureStateA({ theta0: pn.t0, omega0: pn.w0, theta1: pn.t1, omega1: pn.w1 });
        sl.$.pertNextB[idx] = SculptureStateB({ theta2: pn.t2, omega2: pn.w2, theta3: pn.t3, omega3: pn.w3 });
        const dd0 = circDiff(pn.t0 - bn.t0); const dw0 = pn.w0 - bn.w0;
        const dd1 = circDiff(pn.t1 - bn.t1); const dw1 = pn.w1 - bn.w1;
        const dd2 = circDiff(pn.t2 - bn.t2); const dw2 = pn.w2 - bn.w2;
        const dd3 = circDiff(pn.t3 - bn.t3); const dw3 = pn.w3 - bn.w3;
        acc = acc + std.sqrt(dd0*dd0 + dw0*dw0 + dd1*dd1 + dw1*dw1 + dd2*dd2 + dw2*dw2 + dd3*dd3 + dw3*dw3);
      }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: acc, a: 1 });
    } else {
      const sigma = np.perturb;
      let sumLog = 0.0;
      for (let k = 0; k < K; k++) {
        const kf = d.f32(k);
        const h0 = samplePerturb(px * 1000 + np.seed + kf * 131.5, py * 1000 + np.seed, sigma, np.perturbMode);
        const h1 = samplePerturb(px * 1000 + 17 + np.seed + kf * 131.5, py * 1000 + np.seed, sigma, np.perturbMode);
        const h2 = samplePerturb(px * 1000 + 100 + np.seed + kf * 131.5, py * 1000 + np.seed, sigma, np.perturbMode);
        const h3 = samplePerturb(px * 1000 + 217 + np.seed + kf * 131.5, py * 1000 + np.seed, sigma, np.perturbMode);
        const pn = integrate(bA.theta0 + h0, bA.omega0, bA.theta1 + h1, bA.omega1, bB.theta2 + h2, bB.omega2, bB.theta3 + h3, bB.omega3, p.scM0, p.scL0, p.scA0, p.scR, p.scN, dt);
        const dd0 = circDiff(pn.t0 - bn.t0); const dw0 = pn.w0 - bn.w0;
        const dd1 = circDiff(pn.t1 - bn.t1); const dw1 = pn.w1 - bn.w1;
        const dd2 = circDiff(pn.t2 - bn.t2); const dw2 = pn.w2 - bn.w2;
        const dd3 = circDiff(pn.t3 - bn.t3); const dw3 = pn.w3 - bn.w3;
        sumLog = sumLog + std.log(std.max(std.sqrt(dd0*dd0 + dw0*dw0 + dd1*dd1 + dw1*dw1 + dd2*dd2 + dw2*dw2 + dd3*dd3 + dw3*dw3), 1e-12) / sigma);
      }
      const gmLog = sumLog / np.K;
      const data = sl.$.data[ci];
      let count = data.a; let sumv = data.b;
      if (count < 0.5) { count = 1; sumv = gmLog; } else { count = count + 1; sumv = sumv + gmLog; }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: (sumv / count) / dt, a: count });
    }
  };

  return {
    nbInitPipeline: root.createGuardedComputePipeline(nbInitCell),
    nbStepPipeline: root.createGuardedComputePipeline(nbStepCell),
    nbInitBG: root.createBindGroup(initLayout, {
      baseA: o.baseAa, baseB: o.baseBa, pertA: o.nbPertAa, pertB: o.nbPertBa,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
    nbStepFwd: root.createBindGroup(stepLayout, {
      baseCurA: o.baseAa, baseCurB: o.baseBa, baseNextA: o.baseAb, baseNextB: o.baseBb,
      pertCurA: o.nbPertAa, pertCurB: o.nbPertBa, pertNextA: o.nbPertAb, pertNextB: o.nbPertBb,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
    nbStepBwd: root.createBindGroup(stepLayout, {
      baseCurA: o.baseAb, baseCurB: o.baseBb, baseNextA: o.baseAa, baseNextB: o.baseBa,
      pertCurA: o.nbPertAb, pertCurB: o.nbPertBb, pertNextA: o.nbPertAa, pertNextB: o.nbPertBa,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
  };
}
